import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { MazeSystem } from '../systems/MazeSystem';

export enum Direction {
  UP = 0,
  RIGHT = 1,
  DOWN = 2,
  LEFT = 3,
  NONE = 4
}

export class Player implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private mazeSystem: MazeSystem;
  
  // Grid-based movement
  private gridPosition: Vector2;
  private targetPosition: Vector2;
  private currentDirection: Direction = Direction.NONE;
  private nextDirection: Direction = Direction.NONE;
  private isMoving: boolean = false;
  private moveSpeed: number = 80; // pixels per second
  private tileSize: number = 16;
  
  // Animation
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private animationSpeed: number = 8; // frames per second
  
  // Game state
  private score: number = 0;
  private invulnerable: boolean = false;
  private invulnerabilityTime: number = 0;

  constructor(
    id: string,
    gridX: number,
    gridY: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    mazeSystem: MazeSystem
  ) {
    this.id = id;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.mazeSystem = mazeSystem;
    this.tileSize = mazeSystem.getTileSize();
    
    // Initialize position
    this.gridPosition = { x: gridX, y: gridY };
    this.position = mazeSystem.gridToScreen(gridX, gridY);
    this.targetPosition = { ...this.position };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: this.tileSize, y: this.tileSize };
  }

  public update(deltaTime: number): { points: number, powerPellet: boolean } {
    // Update animation
    this.updateAnimation(deltaTime);
    
    // Update invulnerability
    if (this.invulnerable) {
      this.invulnerabilityTime -= deltaTime;
      if (this.invulnerabilityTime <= 0) {
        this.invulnerable = false;
      }
    }
    
    // Handle movement
    this.updateMovement(deltaTime);
    
    // Check for dot collection and return result
    return this.checkDotCollection();
  }

  private updateAnimation(deltaTime: number): void {
    if (this.isMoving) {
      this.animationTime += deltaTime * this.animationSpeed;
      this.animationFrame = Math.floor(this.animationTime) % 3; // 3-frame animation
    } else {
      this.animationFrame = 1; // Default frame when not moving
    }
  }

  private updateMovement(deltaTime: number): void {
    // If not moving, try to start moving in next direction or current direction
    if (!this.isMoving) {
      if (this.nextDirection !== Direction.NONE && this.canChangeDirection()) {
        this.currentDirection = this.nextDirection;
        this.nextDirection = Direction.NONE;
        this.startMoving();
      } else if (this.currentDirection !== Direction.NONE && this.canContinueMoving()) {
        this.startMoving();
      }
    }
    
    // Continue moving if currently moving
    if (this.isMoving) {
      this.moveTowardsTarget(deltaTime);
      
      // Check if reached target
      if (this.hasReachedTarget()) {
        this.snapToGrid();
        
        // Try to change direction if requested, otherwise continue
        if (this.nextDirection !== Direction.NONE && this.canChangeDirection()) {
          this.currentDirection = this.nextDirection;
          this.nextDirection = Direction.NONE;
          this.startMoving();
        } else if (this.canContinueMoving()) {
          this.startMoving();
        } else {
          this.stopMoving();
        }
      }
    }
  }

  private canChangeDirection(): boolean {
    if (this.nextDirection === Direction.NONE) return false;
    
    // Get the direction vector
    const direction = this.getDirectionVector(this.nextDirection);
    
    // Check if this is a tunnel entrance
    if (this.mazeSystem.isTunnelEntrance(this.gridPosition.x, this.gridPosition.y, direction)) {
      return true; // Allow tunnel movement
    }
    
    const nextGridPos = {
      x: this.gridPosition.x + direction.x,
      y: this.gridPosition.y + direction.y
    };
    
    // Handle tunnel wrapping
    const wrappedPos = this.mazeSystem.handleTunnelWrap(nextGridPos.x, nextGridPos.y);
    
    
    return this.mazeSystem.isWalkable(wrappedPos.x, wrappedPos.y);
  }

  private canContinueMoving(): boolean {
    if (this.currentDirection === Direction.NONE) return false;
    
    const direction = this.getDirectionVector(this.currentDirection);
    
    // Check if this is a tunnel entrance
    if (this.mazeSystem.isTunnelEntrance(this.gridPosition.x, this.gridPosition.y, direction)) {
      return true; // Allow tunnel movement
    }
    
    const nextGridPos = {
      x: this.gridPosition.x + direction.x,
      y: this.gridPosition.y + direction.y
    };
    
    // Handle tunnel wrapping
    const wrappedPos = this.mazeSystem.handleTunnelWrap(nextGridPos.x, nextGridPos.y);
    
    
    return this.mazeSystem.isWalkable(wrappedPos.x, wrappedPos.y);
  }

  private startMoving(): void {
    const direction = this.getDirectionVector(this.currentDirection);
    let nextGridPos = {
      x: this.gridPosition.x + direction.x,
      y: this.gridPosition.y + direction.y
    };
    
    // Handle tunnel entrances - instant teleportation
    if (this.mazeSystem.isTunnelEntrance(this.gridPosition.x, this.gridPosition.y, direction)) {
      let teleportPos: { x: number, y: number } | null = null;
      
      if ((this.gridPosition.x === 0 || this.gridPosition.x === 1) && direction.x === -1) {
        // Going left - instantly teleport to right side
        teleportPos = { x: 23, y: this.gridPosition.y };
      } else if ((this.gridPosition.x === this.mazeSystem.getMazeWidth() - 1 || this.gridPosition.x === 23) && direction.x === 1) {
        // Going right - instantly teleport to left side  
        teleportPos = { x: 1, y: this.gridPosition.y };
      }
      
      if (teleportPos) {
        // Instant teleportation - no gradual movement
        this.gridPosition = teleportPos;
        this.position = this.mazeSystem.gridToScreen(teleportPos.x, teleportPos.y);
        this.targetPosition = { ...this.position };
        this.velocity = { x: 0, y: 0 };
        this.isMoving = false;
        
        // Continue moving in the same direction after teleportation
        this.startMoving();
        return;
      }
    }
    
    // Handle normal movement
    const wrappedPos = this.mazeSystem.handleTunnelWrap(nextGridPos.x, nextGridPos.y);
    
    this.gridPosition = wrappedPos;
    this.targetPosition = this.mazeSystem.gridToScreen(wrappedPos.x, wrappedPos.y);
    
    // Set velocity
    const moveDirection = {
      x: this.targetPosition.x - this.position.x,
      y: this.targetPosition.y - this.position.y
    };
    const distance = Math.sqrt(moveDirection.x * moveDirection.x + moveDirection.y * moveDirection.y);
    
    if (distance > 0) {
      this.velocity = {
        x: (moveDirection.x / distance) * this.moveSpeed,
        y: (moveDirection.y / distance) * this.moveSpeed
      };
    }
    
    this.isMoving = true;
  }

  private moveTowardsTarget(deltaTime: number): void {
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  private hasReachedTarget(): boolean {
    const dx = this.targetPosition.x - this.position.x;
    const dy = this.targetPosition.y - this.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < 2; // Close enough threshold
  }

  private snapToGrid(): void {
    this.position = { ...this.targetPosition };
    this.velocity = { x: 0, y: 0 };
    this.isMoving = false;
  }

  private stopMoving(): void {
    this.velocity = { x: 0, y: 0 };
    this.isMoving = false;
    this.currentDirection = Direction.NONE;
  }

  private getDirectionVector(direction: Direction): Vector2 {
    switch (direction) {
      case Direction.UP: return { x: 0, y: -1 };
      case Direction.RIGHT: return { x: 1, y: 0 };
      case Direction.DOWN: return { x: 0, y: 1 };
      case Direction.LEFT: return { x: -1, y: 0 };
      default: return { x: 0, y: 0 };
    }
  }

  private checkDotCollection(): { points: number, powerPellet: boolean } {
    const tile = this.mazeSystem.getTileAt(this.gridPosition.x, this.gridPosition.y);
    if (!tile || tile.collected) return { points: 0, powerPellet: false };
    
    const wasPowerPellet = tile.type === 3; // TileType.POWER_PELLET
    const points = this.mazeSystem.collectDot(this.gridPosition.x, this.gridPosition.y);
    
    if (points > 0) {
      this.score += points;
      
      // Play appropriate sound (but not power pellet sound here)
      if (!wasPowerPellet) {
        this.audioManager.playTone(800, 0.1, 'triangle');
      }
      
      return { points, powerPellet: wasPowerPellet };
    }
    
    return { points: 0, powerPellet: false };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    
    // Flash when invulnerable
    if (this.invulnerable && Math.floor(Date.now() / 100) % 2 === 0) {
      return;
    }

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;
    
    this.drawPlayer(centerX, centerY);
  }

  private drawPlayer(centerX: number, centerY: number): void {
    const color = '#BFCE72'; // C64 Yellow
    
    // Draw Pac-Man based on direction and animation frame
    const radius = 6;
    
    if (this.currentDirection === Direction.NONE) {
      // Draw closed circle when not moving
      this.renderManager.drawCircle(centerX, centerY, radius, true, color);
    } else {
      // Draw Pac-Man with mouth opening/closing
      const mouthAngle = this.animationFrame === 1 ? Math.PI / 3 : Math.PI / 6;
      
      // Calculate mouth direction based on movement
      let startAngle = 0;
      switch (this.currentDirection) {
        case Direction.RIGHT:
          startAngle = -mouthAngle / 2;
          break;
        case Direction.LEFT:
          startAngle = Math.PI - mouthAngle / 2;
          break;
        case Direction.UP:
          startAngle = -Math.PI / 2 - mouthAngle / 2;
          break;
        case Direction.DOWN:
          startAngle = Math.PI / 2 - mouthAngle / 2;
          break;
      }
      
      // Draw Pac-Man arc
      const endAngle = startAngle + (Math.PI * 2 - mouthAngle);
      
      const ctx = this.renderManager.getContext();
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fill();
    }
    
    // Draw eye
    const eyeOffset = 2;
    let eyeX = centerX;
    let eyeY = centerY - eyeOffset;
    
    // Adjust eye position based on direction
    switch (this.currentDirection) {
      case Direction.RIGHT:
        eyeX = centerX + eyeOffset;
        eyeY = centerY - 1;
        break;
      case Direction.LEFT:
        eyeX = centerX - eyeOffset;
        eyeY = centerY - 1;
        break;
      case Direction.DOWN:
        eyeY = centerY + eyeOffset;
        break;
    }
    
    this.renderManager.drawCircle(eyeX, eyeY, 1, true, '#000000');
  }

  // Input handling methods
  public setNextDirection(direction: Direction): void {
    this.nextDirection = direction;
  }

  public getCurrentDirection(): Direction {
    return this.currentDirection;
  }

  public getGridPosition(): Vector2 {
    return { ...this.gridPosition };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public getScore(): number {
    return this.score;
  }

  public addScore(points: number): void {
    this.score += points;
  }

  public setInvulnerable(duration: number): void {
    this.invulnerable = true;
    this.invulnerabilityTime = duration;
  }

  public isInvulnerable(): boolean {
    return this.invulnerable;
  }

  public destroy(): void {
    this.active = false;
    this.audioManager.playExplosion();
  }

  public reset(gridX: number, gridY: number): void {
    this.gridPosition = { x: gridX, y: gridY };
    this.position = this.mazeSystem.gridToScreen(gridX, gridY);
    this.targetPosition = { ...this.position };
    this.velocity = { x: 0, y: 0 };
    this.currentDirection = Direction.NONE;
    this.nextDirection = Direction.NONE;
    this.isMoving = false;
    this.active = true;
    this.invulnerable = false;
    this.invulnerabilityTime = 0;
    this.animationTime = 0;
    this.animationFrame = 0;
  }
}